/**
 * Shared utility functions and rendering helpers used across all views.
 *
 * All functions are pure — no global state, no side effects.
 * They accept data and return HTML strings ready for innerHTML injection.
 */

import type { ToastVariant } from '../types/domain.js'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime.js'
import { createPatch } from 'diff'
import { html as diffHtml } from 'diff2html'

dayjs.extend(relativeTime)

// ---------------------------------------------------------------------------
// Markdown rendering
// ---------------------------------------------------------------------------

/**
 * Render markdown text as sanitized HTML.
 * Uses marked.js for parsing and DOMPurify for XSS sanitisation.
 */
export function renderMd(text: string): string {
  // marked.parse is synchronous in this context; type assertion needed because
  // TS types declare it as string | Promise<string> (covers async overload).
  return DOMPurify.sanitize(marked.parse(String(text ?? '')) as string)
}

// ---------------------------------------------------------------------------
// Relative time
// ---------------------------------------------------------------------------

/**
 * Return a human-readable relative time string (e.g. "3 minutes ago").
 * Uses dayjs for formatting.
 */
export function relTime(iso: string | null | undefined): string {
  if (!iso) {
    return '\u2014'
  }
  return dayjs(String(iso).replace(' ', 'T')).fromNow()
}

// ---------------------------------------------------------------------------
// SVG icons (inline, reused from svg.ts)
// ---------------------------------------------------------------------------

const SVG_CLIPBOARD = `<svg aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="2" width="6" height="4" rx="1"></rect><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path></svg>`

// ---------------------------------------------------------------------------
// Code block rendering
// ---------------------------------------------------------------------------

/** Options for codeBlock() */
export interface CodeBlockOpts {
  filename?: string
  label?: string
  lang?: string
  id?: string
  content: string
}

/** Detect the language of a code block from filename or content. */
export function detectLang(filename: string | undefined, content: string | undefined): string {
  if (!filename && !content) {
    return 'text'
  }
  const ext = (filename ?? '').split('.').pop()?.toLowerCase() ?? ''
  const map: Record<string, string> = {
    py: 'python',
    js: 'javascript',
    ts: 'typescript',
    json: 'json',
    md: 'markdown',
    yaml: 'yaml',
    yml: 'yaml',
    toml: 'toml',
    sh: 'bash',
    bash: 'bash',
    sql: 'sql',
    html: 'html',
    css: 'css',
    rs: 'rust',
    go: 'go',
    java: 'java',
    kt: 'kotlin',
    rb: 'ruby',
    txt: 'text',
    cfg: 'text',
    ini: 'text',
  }
  if (map[ext]) {
    return map[ext]
  }
  // Auto-detect from content
  const s = String(content ?? '').substring(0, 200)
  if (s.startsWith('{') || s.startsWith('[')) {
    return 'json'
  }
  if (/^(def |class |import |from |#!.*python)/m.test(s)) {
    return 'python'
  }
  if (/^(?:function |const |var |let |(?:\([^)]*\)|\w+)\s*=>)/m.test(s)) {
    return 'javascript'
  }
  if (/^#!/m.test(s)) {
    return 'bash'
  }
  return 'text'
}

/**
 * Render a syntax-highlighted code block with optional filename header
 * and copy button.
 */
export function codeBlock(content: string, opts: CodeBlockOpts): string {
  const lang = opts.lang ?? detectLang(opts.filename, opts.content)
  const text = String(content ?? '')
  // Use caller-supplied stable ID when available (e.g. Observe tab cards)
  const id = opts.id ?? `cb-${crypto.randomUUID().replace(/-/g, '').substring(0, 8)}`

  let header = ''
  if (opts.filename || opts.label) {
    header = `<div class="cb-header">
      ${opts.filename ? `<span class="cb-filename">${esc(opts.filename.split('/').pop() ?? '')}</span>` : ''}
      ${opts.label ? `<span class="cb-label">${esc(opts.label)}</span>` : ''}
      <span style="flex:1"></span>
      <span class="cb-lang">${esc(lang)}</span>
      <button class="cb-copy" onclick="_cbCopy('${escJs(id)}')" title="Copy">${SVG_CLIPBOARD} Copy</button>
    </div>`
  }

  return `<div class="cb-container">${header}
    <pre class="line-numbers" id="${id}" data-raw="${esc(text).replace(/"/g, '&quot;')}">
      <code class="language-${lang}">${esc(text)}</code></pre></div>`
}

/**
 * Render an interactive unified diff between two strings.
 * Uses jsdiff + Diff2html.
 */
export function codeDiff(
  oldStr: string | null | undefined,
  newStr: string | null | undefined,
  opts?: { filename?: string },
): string {
  const oldS = oldStr ?? ''
  const newS = newStr ?? ''
  const diffInput = createPatch('file', oldS, newS)
  const isDark = !document.body.classList.contains('theme-light')
  const diffHtmlOutput = diffHtml(diffInput, {
    drawFileList: false,
    matching: 'lines',
    outputFormat: 'line-by-line',
    // diff2html types expect ColorSchemeType enum, but string literals work at runtime
    // @ts-expect-error -- ColorSchemeType is type-only; string literals are valid at runtime
    // (tracked: diff2html has a known type mismatch between enum and string literal types)
    colorScheme: isDark ? 'dark' : 'light',
  })
  const filename = opts?.filename ? opts.filename.split('/').pop() : ''
  // Sanitize diff2html output through DOMPurify as defense-in-depth.
  const safeDiff = DOMPurify.sanitize(diffHtmlOutput, {
    ALLOWED_ATTR: ['class', 'style', 'href', 'title', 'id', 'data-content'],
    ALLOW_DATA_ATTR: true,
  })
  return `<div class="cb-container">
    <div class="cb-header">
      ${filename ? `<span class="cb-filename">${esc(filename)}</span>` : ''}
      <span class="cb-label">diff</span>
      <span style="flex:1"></span>
      <span class="cb-lang">diff</span>
    </div>
    <div class="d2h-wrapper" style="overflow:auto;max-height:60vh">${safeDiff}</div>
  </div>`
}

// ---------------------------------------------------------------------------
// HTML escaping
// ---------------------------------------------------------------------------

/** Escape HTML special characters to prevent XSS. */
export function esc(s: string): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/** Escape a string for use inside a JS single-quoted string literal. */
export function escJs(s: string): string {
  return String(s ?? '')
    .replace(/\\/g, '\\\\')
    .replace(/'/g, "\\'")
}

// ---------------------------------------------------------------------------
// Toast notifications
// ---------------------------------------------------------------------------

/** Show a toast notification. */
export function showToast(message: string, variant: ToastVariant = 'info'): void {
  const container = getOrCreateToastContainer()
  const toast = document.createElement('div')
  toast.className = `toast toast-${variant}`
  toast.setAttribute('role', 'status')
  toast.setAttribute('aria-live', 'polite')
  toast.textContent = message
  container.appendChild(toast)
  // Force reflow so CSS transition applies
  toast.offsetHeight
  toast.classList.add('toast-visible')
  setTimeout(() => {
    toast.classList.remove('toast-visible')
    toast.addEventListener(
      'transitionend',
      () => {
        toast.remove()
      },
      { once: true },
    )
  }, 2000)
}

function getOrCreateToastContainer(): HTMLElement {
  let container = document.getElementById('toast-container')
  if (!container) {
    container = document.createElement('div')
    container.id = 'toast-container'
    container.setAttribute('aria-live', 'polite')
    container.style.cssText =
      'position:fixed;bottom:1rem;right:1rem;z-index:9999;display:flex;flex-direction:column;gap:0.5rem;pointer-events:none;'
    document.body.appendChild(container)
  }
  return container
}

// ---------------------------------------------------------------------------
// Formatting utilities
// ---------------------------------------------------------------------------

/** Format a USD cost value, e.g. 1.234 -> "$1.23" */
export function formatCost(usd: number | null | undefined): string {
  if (usd === null || usd === undefined) {
    return '\u2014'
  }
  return `$${usd.toFixed(2)}`
}

/** Format a token count, e.g. 42100 -> "42.1K" */
export function formatTokens(tokens: number | null | undefined): string {
  if (tokens === null || tokens === undefined) {
    return '\u2014'
  }
  if (tokens >= 1_000_000) {
    return `${(tokens / 1_000_000).toFixed(1)}M`
  }
  if (tokens >= 1_000) {
    return `${(tokens / 1_000).toFixed(1)}K`
  }
  return String(tokens)
}

/** Format a duration in seconds as a human-readable string, e.g. 3720 -> "1h 2m" */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) {
    return '\u2014'
  }
  if (seconds < 60) {
    return `${seconds}s`
  }
  if (seconds < 3600) {
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return s > 0 ? `${m}m ${s}s` : `${m}m`
  }
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  return m > 0 ? `${h}h ${m}m` : `${h}h`
}

// ---------------------------------------------------------------------------
// Clipboard helpers
// ---------------------------------------------------------------------------

/** Copy text to clipboard using navigator.clipboard API. */
export async function copyToClipboard(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text)
    showToast('Copied', 'success')
  } else {
    showToast('Copy not supported', 'error')
  }
}
